import React from 'react';
import { theme } from '../../../theme';
import { Star, User } from 'lucide-react';
import { VenueReview } from '../../../types/venue';
import { formatDate } from '../../../utils/venueHelpers';

interface VenueReviewCardProps {
  review: VenueReview;
}

const VenueReviewCard: React.FC<VenueReviewCardProps> = ({ review }) => {
  const renderStars = (rating: number) => {
    return (
      <div style={{ display: 'flex', gap: 4 }}>
        {Array.from({ length: 5 }, (_, i) => (
          <Star
            key={i}
            size={16}
            fill={i < rating ? theme.colors.warning : 'none'}
            stroke={i < rating ? theme.colors.warning : theme.colors.border}
          />
        ))}
      </div>
    );
  };

  const cardStyle: React.CSSProperties = {
    backgroundColor: theme.colors.surface,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.card,
    padding: 20,
    marginBottom: 16,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 12,
  };

  const userInfoStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  };

  const avatarStyle: React.CSSProperties = {
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: theme.colors.primary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#000',
    fontSize: 18,
    fontWeight: theme.fonts.weightBold,
  };

  const userNameStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    fontWeight: theme.fonts.weightBold,
    color: theme.colors.text,
    marginBottom: 4,
  };

  const dateStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const ratingsGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gap: 12,
    marginBottom: 16,
  };

  const ratingItemStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  };

  const ratingLabelStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeSmall,
    color: theme.colors.textSecondary,
  };

  const commentStyle: React.CSSProperties = {
    fontSize: theme.fonts.sizeBody,
    color: theme.colors.text,
    lineHeight: 1.6,
    padding: 12,
    backgroundColor: theme.colors.background,
    borderRadius: theme.borderRadius.input,
  };

  return (
    <div style={cardStyle}>
      <div style={headerStyle}>
        <div style={userInfoStyle}>
          <div style={avatarStyle}>
            {review.reviewer?.avatar ? (
              <img
                src={review.reviewer.avatar}
                alt={review.reviewer.name}
                style={{ width: '100%', height: '100%', borderRadius: '50%', objectFit: 'cover' }}
              />
            ) : (
              <User size={20} />
            )}
          </div>
          <div>
            <div style={userNameStyle}>
              {review.reviewer?.name || 'Usuario'}
            </div>
            <div style={dateStyle}>
              {formatDate(review.createdAt)}
            </div>
          </div>
        </div>
        {renderStars(review.rating)}
      </div>

      {/* Specific Ratings */}
      {(review.surfaceQuality || review.lightingQuality || review.facilities || review.valueForMoney) && (
        <div style={ratingsGridStyle}>
          {review.surfaceQuality && (
            <div style={ratingItemStyle}>
              <span style={ratingLabelStyle}>Superficie</span>
              {renderStars(review.surfaceQuality)}
            </div>
          )}
          {review.lightingQuality && (
            <div style={ratingItemStyle}>
              <span style={ratingLabelStyle}>Iluminación</span>
              {renderStars(review.lightingQuality)}
            </div>
          )}
          {review.facilities && (
            <div style={ratingItemStyle}>
              <span style={ratingLabelStyle}>Instalaciones</span>
              {renderStars(review.facilities)}
            </div>
          )}
          {review.valueForMoney && (
            <div style={ratingItemStyle}>
              <span style={ratingLabelStyle}>Calidad-precio</span>
              {renderStars(review.valueForMoney)}
            </div>
          )}
        </div>
      )}

      {review.comment && (
        <div style={commentStyle}>
          {review.comment}
        </div>
      )}
    </div>
  );
};

export default VenueReviewCard;
